import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AiEventType, AgentMessageEvent, UserMessageEvent } from "../../../types/ai-event";

interface TextBlockProps {
  event: AgentMessageEvent | UserMessageEvent;
  // Overrides the role-based test id (a queued message is a user bubble that is not yet a msg-user).
  testId?: string;
}

const TextBlockView: React.FC<TextBlockProps> = ({ event, testId }) => {
  const theme = useTheme();
  const isUser = event.type === AiEventType.USER_MESSAGE;

  return (
    <Box
      data-testid={testId ?? (isUser ? "msg-user" : "msg-agent")}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
        backgroundColor: isUser
          ? theme.palette.action.selected
          : theme.palette.mode === "dark"
          ? "rgba(232, 75, 250, 0.14)"
          : "rgba(232, 75, 250, 0.08)",
        color: theme.palette.text.primary,
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: isUser ? "85%" : "100%",
        minWidth: 0,
        overflowWrap: "anywhere",
        fontSize: "0.8rem",
        "& p": { m: 0, fontSize: "0.8rem", lineHeight: 1.5 },
        "& p + p": { mt: 0.75 },
        "& li": { fontSize: "0.8rem", lineHeight: 1.5 },
        "& h1, & h2, & h3": { fontSize: "0.85rem", fontWeight: 600, mt: 1, mb: 0.5 },
        "& code": {
          backgroundColor: theme.palette.action.hover,
          px: 0.5,
          borderRadius: 0.5,
          fontSize: "0.85em",
          fontFamily: "monospace",
        },
        "& pre": {
          backgroundColor: theme.palette.action.hover,
          p: 1,
          borderRadius: 1,
          overflow: "auto",
          "& code": { backgroundColor: "transparent", p: 0 },
        },
      }}
    >
      {isUser ? (
        <Typography variant="body2">{event.content}</Typography>
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {event.content}
        </ReactMarkdown>
      )}
    </Box>
  );
};

// agent-chat-speed Decision 3: a message's look depends only on its role and its text, so an
// unchanged message skips ReactMarkdown + remark-gfm when the list re-renders; a streamed delta
// changes `content` and re-renders exactly that message.
export const TextBlock = React.memo(
  TextBlockView,
  (prev, next) =>
    prev.event.type === next.event.type && prev.event.content === next.event.content && prev.testId === next.testId
);
